import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  ADDRESS_SERIALIZER,
  Address,
  CommandService,
  ConverterService,
  OccEndpointsService,
  Region, StateWithProcess, StateWithUser, UserActions,
  UserAddressService, UserIdService
} from '@spartacus/core';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CustomUserAddressConnector } from '../../connectors/address/custom-user-address.connector';
import { CustomAddresses, CustomRegions } from '../../store/actions';
import { CustomLoadDeliveryCountriesAll } from '../../store/actions/custom-delivery-countries.action';
import { StateWithUserRegionState } from '../../store/reducers/regions-zone.reducer';
import { StateWithUserAddressState } from '../../store/reducers/user-addresses-zone.reducer';
import { getRegionsDataAndLoading } from '../../store/selectors/regions-zone.selectors';
import {
  getAddressesByZoneLoadedSuccess,
  getAddressesByZoneLoading, getAddressesDataAndLoading
} from '../../store/selectors/user-addresses.selectors';

@Injectable({
  providedIn: 'root',
})

export class CustomUserAddressService extends UserAddressService implements OnDestroy {
  protected subscriptions: Subscription = new Subscription();

  constructor(
    protected store: Store<StateWithUser>,
    protected userIdService: UserIdService,
    protected userAddressConnector: CustomUserAddressConnector,
    protected command: CommandService,
    protected storeRegion: Store<StateWithUserRegionState | StateWithProcess<void>>,
    protected storeAddresses: Store<StateWithUserAddressState | StateWithProcess<void>>,
    protected activeCartService: ActiveCartFacade,
    protected http: HttpClient,
    protected occEndpoints: OccEndpointsService,
    protected converter: ConverterService
  ) {
    super(store, userIdService, userAddressConnector, command);
  }

  loadDeliveryCountriesAll(): void {
    this.store.dispatch(new CustomLoadDeliveryCountriesAll());
  }

  clearUserMiscData(): void {
    this.store.dispatch(new UserActions.ClearUserMiscsData());
  }
  /**
   * Sets user address as default
   * @param addressId a user address ID,
   * @param address
   */
  customSetAddressAsDefault(addressId: string, address: Address): void {
    this.subscriptions.add(this.userIdService.takeUserId().subscribe((userId) => {
      this.store.dispatch(
        new UserActions.UpdateUserAddress({
          userId,
          addressId,
          address,
        })
      );
    }));
  }


  loadRegionsByZone(countryIsoCode: string, zone?: string): void {
    this.storeRegion.dispatch(new CustomRegions.LoadRegions({ countryIsoCode, zone }));
  }

  /**
   * Clear regions in store - useful when changing country
   */
  clearRegionsByZone(): void {
    this.storeRegion.dispatch(new CustomRegions.ClearRegions());
  }
  /**
   * Returns all regions
   */
  getRegionsByZone(countryIsoCode: string, zoneCode?: string): Observable<Region[]> {
    return this.storeRegion.pipe(
      select(getRegionsDataAndLoading),
      map(({ regions, country, loading, loaded, zone }) => {
        if (!countryIsoCode && (loading || loaded)) {
          this.clearRegionsByZone();
          return [];
        } else if (loading && !loaded) {
          // don't interrupt loading
          return [];
        } else if (!loading && ((countryIsoCode !== country && countryIsoCode) || (zone !== zoneCode && zoneCode))) {
          // country changed - clear store and load new regions
          if (country) {
            this.clearRegionsByZone();
          }
          if (zone) {
            this.clearRegionsByZone();
          }
          this.loadRegionsByZone(countryIsoCode, zoneCode);
          return [];
        }
        return regions;
      })
    );
  }


  /**
   * Retrieves user's addresses by zone
   */
  loadAddressesByZone(zone?: string): void {
    if (!zone) {
      this.activeCartService.getActive().subscribe((data) => {
        const zoneCode = !!data?.deliveryZone?.code ? data?.deliveryZone?.code : 'continentalEurope';
        this.userIdService.takeUserId(false).subscribe((userId) => {
          this.storeAddresses.dispatch(new CustomAddresses.LoadUserAddresses({ userId, zone: zoneCode }));
        }).unsubscribe();
      }).unsubscribe();
    } else {
      // Change takeUserId to true to block for only logged in
      this.userIdService.takeUserId(false).subscribe((userId) => {
        this.storeAddresses.dispatch(new CustomAddresses.LoadUserAddresses({ userId, zone }));
      }).unsubscribe();
    }
  }


  /**
   * Updates existing user address by zone
   * @param addressId a user address ID
   * @param address a user address
   */
  updateUserAddressByZone(addressId: string, address: Address): void {
    this.subscriptions.add(this.activeCartService.getActive().subscribe(
      (data) => {
        const zone = !!data?.deliveryZone?.code ? data?.deliveryZone?.code : 'continentalEurope';

        this.subscriptions.add(this.userIdService.takeUserId(false).subscribe((userId) => {
          this.storeAddresses.dispatch(
            new CustomAddresses.UpdateUserAddress({
              userId,
              addressId,
              address,
              zone
            })
          );
        }));
      }));
  }


  /**
   * Sets user address as default by zone
   * @param addressId a user address ID
   */
  setAddressAsDefaultByZone(addressId: string): void {
    this.subscriptions.add(this.activeCartService.getActive().subscribe(
      (data) => {
        const zone = !!data?.deliveryZone?.code ? data?.deliveryZone?.code : 'continentalEurope';

        this.subscriptions.add(this.userIdService.takeUserId(false).subscribe((userId) => {
          this.storeAddresses.dispatch(
            new CustomAddresses.UpdateUserAddress({
              userId,
              addressId,
              address: { defaultAddress: true },
              zone
            })
          );
        }));
      }));
  }

  /**
   * Returns addresses by zone
   */
  getAddressesByZone(): Observable<Address[]> {
    return this.activeCartService.getActive().pipe(
      map((data) => !!data?.deliveryZone?.code ? data?.deliveryZone?.code : 'continentalEurope'),
      switchMap((zoneCode) => {
        return this.storeAddresses.pipe(
          select(getAddressesDataAndLoading),
          map(({ addresses, loading, loaded, zone }) => {
            if (loading && !loaded) {
              // don't interrupt loading
              return [];
            } else if (!loading && (zone !== zoneCode && zoneCode)) {
              if (zone) {
                this.clearRegionsByZone();
              }
              this.loadAddressesByZone(zoneCode);
              return [];
            }
            return addresses;
          })
        );
      }));
  }

  /**
   * Returns a loading flag for addresses by zone
   */
  getAddressesByZoneLoading(): Observable<boolean> {
    return this.storeAddresses.pipe(select(getAddressesByZoneLoading));
  }

  getAddressesByZoneLoadedSuccess(): Observable<boolean> {
    return this.storeAddresses.pipe(select(getAddressesByZoneLoadedSuccess));
  }

  updateAddress(userId: string, addressId: string, address: Address): Observable<{}> {
    const url = this.occEndpoints.buildUrl('addressCustomUpdate', {
      urlParams: {
        userId,
        addressId
      }
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    address = this.converter.convert(address, ADDRESS_SERIALIZER);

    return this.http
      .put(url, address, { headers })
      .pipe(catchError((error: any) => throwError(error)));
  }

  /**
   * Clear addresses in store
   */
  clearAddresses(): void {
    this.storeRegion.dispatch(new CustomAddresses.ClearAddresses());
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
